import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ShieldCheck, Users, TrendingUp, Building2, Compass, BadgeCheck, Wrench } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { NewsCard } from "@/pages/NewsList";
import { api } from "@/lib/api";

const HERO = "https://static.prod-images.emergentagent.com/jobs/c5e91a2c-dfe3-4217-9721-17960b2be9f3/images/e6bf56106af5c2fc113963cbfb91095b4e48990204b2e7482dacf831ccecd685.jpeg";

const steps = [
  { no: "01", title: "Claim", icon: BadgeCheck, desc: "Verify your business with GST." },
  { no: "02", title: "Build", icon: Wrench, desc: "Create your business presence and share what you offer." },
  { no: "03", title: "Connect", icon: Users, desc: "Discover and connect with businesses across India." },
];

const pillars = [
  { icon: Compass, title: "Discover", desc: "Find verified businesses across India by name, city, or category in seconds.", span: "md:col-span-2" },
  { icon: Users, title: "Connect", desc: "Build your professional circle with trusted, GST-verified partners.", span: "" },
  { icon: TrendingUp, title: "Grow", desc: "Showcase your business profile and reach new customers nationwide.", span: "" },
  { icon: ShieldCheck, title: "Trust First", desc: "Every profile carries a GST verification badge so you know who you're dealing with.", span: "md:col-span-2" },
];

export default function Landing() {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  useEffect(() => {
    api.get("/news", { params: { limit: 3 } }).then(({ data }) => setNews(data)).catch(() => {});
  }, []);
  return (
    <Layout>
      <Helmet>
        <title>IndiaBusinessCircle | India's Verified Business Network</title>
        <meta name="description" content="India's verified business network. Create your GST-verified business presence, discover businesses, and build valuable connections across India." />
      </Helmet>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center py-16 sm:py-24 lg:py-28">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-light px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-brand mb-6">
              India's Verified Business Network
            </span>
            <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05]">
              Discover.{" "}
              <span className="text-brand">Connect.</span>{" "}
              Grow.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-lg">
              India's verified business network. Create your GST-verified business presence, showcase what you offer, discover businesses, and build valuable connections across India.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button data-testid="hero-get-started-btn" onClick={() => navigate("/register")} className="rounded-full bg-brand hover:bg-brand-hover px-7 h-12 text-base font-semibold gap-2 transition-transform hover:-translate-y-0.5">
                Claim Your Business <ArrowRight size={18} />
              </Button>
              <Button data-testid="hero-explore-btn" onClick={() => navigate("/discover")} variant="outline" className="rounded-full px-7 h-12 text-base font-semibold border-slate-300 hover:border-brand hover:text-brand transition-colors">
                Explore Businesses
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="relative"
          >
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl bg-white">
              <img src={HERO} alt="Verified business network connecting manufacturers, distributors, retailers and service providers" className="w-full h-[380px] lg:h-[460px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-3 sm:left-6 bg-white rounded-xl border border-slate-200 shadow-lg px-5 py-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[#DCFCE7] flex items-center justify-center">
                <ShieldCheck size={20} className="text-[#22C55E]" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900">GST Verified</p>
                <p className="text-xs text-slate-500">Know who you're connecting with</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 3-step guide */}
      <section className="bg-white border-t border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Your business, in one place</span>
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <motion.div
                key={s.no}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`step-${s.title.toLowerCase()}`}
                className="relative rounded-xl border border-slate-200 bg-slate-50 p-8"
              >
                <span className="font-display font-black text-5xl text-brand/15 absolute top-5 right-6">{s.no}</span>
                <div className="h-12 w-12 rounded-xl bg-brand-light flex items-center justify-center mb-5">
                  <s.icon size={24} className="text-brand" />
                </div>
                <h3 className="font-display font-bold text-xl text-slate-900">{s.no} — {s.title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars bento */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
          <div className="max-w-2xl mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Why IndiaBusinessCircle</span>
            <h2 className="mt-3 font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-slate-900">
              Everything you need to be found and trusted.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {pillars.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`${p.span} bg-white border border-slate-200 rounded-xl p-8 hover:border-brand/40 transition-colors`}
              >
                <div className="h-12 w-12 rounded-xl bg-brand-light flex items-center justify-center mb-5">
                  <p.icon size={24} className="text-brand" />
                </div>
                <h3 className="font-display font-bold text-xl text-slate-900">{p.title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest News */}
      {news.length > 0 && (
        <section className="bg-slate-50 border-y border-slate-200">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
            <div className="flex items-end justify-between gap-4 mb-10">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">News & Insights</span>
                <h2 className="mt-2 font-display font-black tracking-tight text-3xl sm:text-4xl text-slate-900">Latest News</h2>
              </div>
              <Link to="/news" data-testid="home-news-all" className="shrink-0 inline-flex items-center gap-1.5 text-sm font-bold text-brand hover:gap-2.5 transition-all">
                View all <ArrowRight size={16} />
              </Link>
            </div>
            <div data-testid="home-news-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {news.map((a) => (
                <NewsCard key={a.id} article={a} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
        <div className="rounded-2xl bg-brand px-8 sm:px-16 py-14 sm:py-20 text-center relative overflow-hidden">
          <Building2 className="absolute -right-6 -bottom-6 text-white/10" size={200} aria-hidden="true" />
          <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl text-white relative">
            Ready to grow your circle?
          </h2>
          <p className="mt-4 text-blue-100 max-w-xl mx-auto relative">
            Create your verified business profile today. It's free to get started.
          </p>
          <Button data-testid="cta-signup-btn" onClick={() => navigate("/register")} className="mt-8 rounded-full bg-white text-brand hover:bg-blue-50 px-8 h-12 text-base font-bold relative transition-transform hover:-translate-y-0.5">
            Claim Your Business
          </Button>
        </div>
      </section>
    </Layout>
  );
}
